import { CloudEvent } from './types';

export const SSE_EVENT_TYPES = {
  // Demo event (for testing only)
  DEMO_EVENT: 'demo-event',
  // Account status update event (real-time trading metrics)
  ACCOUNT_STATUS_UPDATE: 'alaric.account.events',
  // Daily loss reached event
  DAILY_LOSS_REACHED: 'alaric.closeout.dailylossreached',
  // Max loss reached event
  MAX_LOSS_REACHED: 'alaric.closeout.totallossreached',
  // Profit target reached event
  PROFIT_TARGET_REACHED: 'alaric.closeout.profittargetreached',
  // Daily auto closs event (closing all positions when market is closing)
  DAILY_AUTO_CLOSE: 'alaric.closeout.dailyautoclose',
} as const;

interface BaseSSEEvent {
  type: string;
  specversion?: string;
  id?: string;
  datacontenttype?: string;
  source?: string;
  subject?: string;
  time?: string;
}

export interface DemoEvent extends BaseSSEEvent {
  type: typeof SSE_EVENT_TYPES.DEMO_EVENT;
  data: {} | null;
}

export interface AccountStatusUpdateEvent extends BaseSSEEvent {
  type: typeof SSE_EVENT_TYPES.ACCOUNT_STATUS_UPDATE;
  data: {
    tradeEvent: {
      dailyLoss: number;
      maxLoss: number;
      planTargetsReached: boolean;
      profitTargetReached: boolean;
      isTradingPlanCompleted: boolean;
      unrealizedPnl: number;
      realizedPnl: number;
    };
    dailyResetEvent?: {
      isCompleted: boolean;
      rewindCount: number;
    } | null;
  };
}

export interface DailyLossReachedEvent extends BaseSSEEvent {
  type: typeof SSE_EVENT_TYPES.DAILY_LOSS_REACHED;
  data: {
    signalId: string;
    accountReferenceId: string;
    reason: number;
    tenantId: number;
    openPositionCount: number;
    closingPositionsCount: number;
    closingPositionSymbols: string | null;
    triggeredAt: string;
    completedAt: string;
    triggeredByUserId: string | null;
    finalAccountStatus: any | null;
    performanceSnapshot: any | null;
  };
}

export interface MaxLossReachedEvent extends BaseSSEEvent {
  type: typeof SSE_EVENT_TYPES.MAX_LOSS_REACHED;
  data: {
    signalId: string;
    accountReferenceId: string;
    reason: number;
    tenantId: number;
    openPositionCount: number;
    closingPositionsCount: number;
    closingPositionSymbols: string | null;
    triggeredAt: string;
    completedAt: string;
    triggeredByUserId: string | null;
    finalAccountStatus: any | null;
    performanceSnapshot: any | null;
  };
}

export interface ProfitTargetReachedEvent extends BaseSSEEvent {
  type: typeof SSE_EVENT_TYPES.PROFIT_TARGET_REACHED;
  data: {
    signalId: string;
    accountReferenceId: string;
    reason: number;
    tenantId: number;
    openPositionCount: number;
    closingPositionsCount: number;
    closingPositionSymbols: string | null;
    triggeredAt: string;
    completedAt: string | null;
    triggeredByUserId: string | null;
    finalAccountStatus: any | null;
    performanceSnapshot: {
      highestPositiveRealizedPnl: number;
      totalPositiveRealizedPnl: number;
      consistencyScore: number;
    } | null;
  };
}

export type SSEEvent =
  | DemoEvent
  | AccountStatusUpdateEvent
  | DailyLossReachedEvent
  | MaxLossReachedEvent
  | ProfitTargetReachedEvent
  | CloudEvent; // Fallback for unknown event types
